import { Injectable } from '@angular/core';
import { Selector, State, Store } from '@ngxs/store';
import * as _ from 'lodash';
import { EMPTY } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { OpenDynamicLinkActionSettingRemoteDto } from 'src/generated/signalR/open-dynamic-link-action-setting-remote-dto';
import { OpenDynamicLinkAutoOpenTrigger } from 'src/generated/signalR/open-dynamic-link-auto-open-trigger';
import { CallTagsSelectors } from '../call-tags/call-tags.selectors';
import { SamwinLinesStateSelector } from '../samwin-lines/samwin-lines.selector';

@State<{}>({
   name: 'openDynamicLink',
   defaults: {},
})
@Injectable()
export class OpenDynamicLinkState {
   constructor() {}

   @Selector([CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn])
   public static canOpenDynamicLinkFn(replaceAllTagsInTextFn: (inputText: string, urlEncode: boolean) => any): (url: string) => boolean {
      return (url: string) => {
         const resolvedUrl = replaceAllTagsInTextFn(url, true);
         return resolvedUrl.allReplacedTagsFound && !_.isEmpty(_.trim(resolvedUrl.text));
      };
   }

   /**
    * Creates an observable which emits a value every time a configured trigger condition matches.
    */
   public static emitOnAutoExecuteTrigger$(store: Store, actionSettings: OpenDynamicLinkActionSettingRemoteDto) {
      // no need to observe states when no triggers are defined
      if (_.isEmpty(actionSettings.autoOpenTriggers)) {
         return EMPTY;
      }

      return store.select(SamwinLinesStateSelector.getSourceLineState).pipe(
         pairwise(),
         filter(([previousState, newState]) => {
            const currentCall = store.selectSnapshot(SamwinLinesStateSelector.getCurrentCall);
            if (_.isNil(currentCall)) {
               return false;
            }

            const isInbound = (currentCall.ici.ici & IciFlags.INBOUND) !== 0;
            const triggers = _.uniq(actionSettings.autoOpenTriggers);

            return isInbound
               ? OpenDynamicLinkState.checkInboundTriggers(triggers, previousState, newState)
               : OpenDynamicLinkState.checkOutboundTriggers(triggers, previousState, newState);
         })
      );
   }

   private static checkInboundTriggers(triggers, previousState: LineStateEnum, newState: LineStateEnum): boolean {
      // inbound call offered
      if (triggers.includes(OpenDynamicLinkAutoOpenTrigger.InboundCallOffered) && previousState === LineStateEnum.IDLE && newState === LineStateEnum.ACCEPTED) {
         return true;
      }

      // inbound call answered
      if (triggers.includes(OpenDynamicLinkAutoOpenTrigger.InboundCallAnswered) && !previousState.isConnected && newState.isConnected) {
         return true;
      }

      // inbound call removed
      if (triggers.includes(OpenDynamicLinkAutoOpenTrigger.InboundAnsweredCallIdle) && previousState.isConnected && !newState.isConnected) {
         return true;
      }

      return false;
   }

   private static checkOutboundTriggers(triggers, previousState: LineStateEnum, newState: LineStateEnum): boolean {
      // outbound call dialed
      if (triggers.includes(OpenDynamicLinkAutoOpenTrigger.OutboundCallDialed) && previousState === LineStateEnum.IDLE && newState === LineStateEnum.RINGBACK) {
         return true;
      }

      // outbound call connected
      if (triggers.includes(OpenDynamicLinkAutoOpenTrigger.OutboundCallConnected) && !previousState.isConnected && newState.isConnected) {
         return true;
      }

      // outbound call idle
      if (triggers.includes(OpenDynamicLinkAutoOpenTrigger.OutboundConnectedCallIdle) && previousState.isConnected && !newState.isConnected) {
         return true;
      }

      return false;
   }
}
